import { useContext, useEffect } from 'react';

import type { SignIn } from '@sorare/wallet-shared';
import { MessagingContext } from '@sorare/wallet-shared';
import type { AcceptTermsInfo, SignedInInfo } from 'contexts/connection';
import { useConnectionContext } from 'contexts/connection';
import { useSignInMutation } from 'contexts/currentUser/useSignIn';
import { sendSafeError } from 'lib/error';
import { useEvents } from 'lib/events/useEvents';

import { useOnSignedIn } from './useOnSignedIn';

const RATE_LIMIT_STATUS_CODE = 429;

export const useSignIn = () => {
  const { registerHandler } = useContext(MessagingContext)!;
  const track = useEvents();
  const { prompt2fa, promptNewDeviceConfirmation, promptTerms } =
    useConnectionContext();
  const signIn = useSignInMutation();

  const onSignedIn = useOnSignedIn();

  useEffect(
    () =>
      registerHandler<SignIn>('signIn', async ({ email, passwordHash }) => {
        try {
          const response = await signIn({
            email,
            password: passwordHash,
          });
          if (!response) return { error: 'Unexpected' };

          const { otpSessionChallenge, currentUser, errors, tcuToken } =
            response;
          const firstErrorMessage = errors[0]?.message;
          if (firstErrorMessage) {
            if (firstErrorMessage === 'authenticate_from_new_device') {
              promptNewDeviceConfirmation();
              return {};
            }
            if (firstErrorMessage === 'must_accept_tcus') {
              await new Promise<AcceptTermsInfo>((resolve, reject) => {
                return promptTerms(
                  {
                    closable: false,
                    tcuToken,
                  },
                  { resolve, reject }
                );
              });
              return {};
            }
            if (firstErrorMessage === 'unconfirmed') {
              track('View Unconfirmed Email Error On SignUp');
            }
            if (!otpSessionChallenge) {
              return { error: errors.map(e => e.message).join(', ') };
            }
          }

          if (otpSessionChallenge) {
            // get challenge reason
            const reason = errors.length === 1 ? firstErrorMessage : undefined;

            const res = await new Promise<SignedInInfo>((resolve, reject) => {
              return prompt2fa(
                { resolve, reject },
                otpSessionChallenge,
                reason
              );
            });
            return onSignedIn(res, passwordHash);
          }

          if (!currentUser) return { error: 'Unexpected' };

          return onSignedIn(currentUser, passwordHash);
        } catch (error: any) {
          if (error === 'has_accepted_tcus_after_2FA') return {};
          if (error.networkError?.statusCode === RATE_LIMIT_STATUS_CODE)
            return { error: 'rate-limit' };
          sendSafeError(error);
          return { error };
        }
      }),
    [
      registerHandler,
      signIn,
      track,
      onSignedIn,
      prompt2fa,
      promptNewDeviceConfirmation,
      promptTerms,
    ]
  );
};
